/* eslint-disable valid-jsdoc */
// frontend/packages/common/utils/widgetLogger.js

/**
 * Système de logging unifié pour les widgets
 * - Logs conditionnés selon l'environnement
 * - Format cohérent
 * - Niveaux de logging appropriés
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Configuration du logging par type
const LOG_CONFIG = {
  // En production, seules les erreurs critiques sont loggées
  production: {
    api: false,
    error: false, // Seules les erreurs vraiment critiques
    warn: false,
    info: false,
    debug: false,
  },
  // En développement, logging sélectif
  development: {
    api: true, // Appels API importants seulement
    error: true, // Toutes les erreurs
    warn: true, // Avertissements importants
    info: false, // Infos désactivées par défaut
    debug: false, // Debug désactivé par défaut
  },
};

const currentConfig = isDevelopment ? LOG_CONFIG.development : LOG_CONFIG.production;

// Contexte des erreurs en cours pour éviter la redondance
let currentErrorContext = null;

/**
 * Logger principal pour les widgets
 */
export const widgetLogger = {
  /**
   * Log des erreurs importantes (toujours en dev, jamais en prod pour les widgets)
   */
  error: (message, data = null) => {},

  /**
   * Log des avertissements (API key manquante, etc.)
   */
  warn: (message, data = null) => {},

  /**
   * Log des appels API importants (succès/échec)
   */
  api: {
    request: (method, url, hasApiKey = false) => {},

    success: (method, url, status) => {
      currentErrorContext = null;
    },

    error: (method, url, status, error = null) => {},
  },

  /**
   * Log d'informations générales (désactivé par défaut)
   */
  info: (message, data = null) => {},

  /**
   * Log de debug détaillé (désactivé par défaut)
   */
  debug: (message, data = null) => {},
};

/**
 * Logger spécialisé pour les erreurs d'authentification
 */
export const authLogger = {
  missingApiKey: () => {
    currentErrorContext = 'auth';
    widgetLogger.warn('🔐 Configuration : API key manquante');
  },

  invalidApiKey: (apiKey) => {
    currentErrorContext = 'auth';
    widgetLogger.warn(`🔐 Configuration : Format de clé API invalide (${apiKey?.substring(0, 8)}...)`);
  },

  authenticationError: () => {
    // Ne log que si on n'a pas déjà loggé un problème d'auth
    if (currentErrorContext !== 'auth') {
      widgetLogger.warn('🔐 Authentification échouée - Vérifiez votre API key');
    }
  },

  accessDenied: () => {
    widgetLogger.warn('🔐 Accès interdit - Permissions insuffisantes');
  },
};

/**
 * Configuration dynamique du logging (pour debug temporaire)
 */
export const configureLogging = (config) => {
  if (isDevelopment) {
    Object.assign(currentConfig, config);
    widgetLogger.info('Configuration de logging mise à jour', currentConfig);
  }
};

// Export de la configuration actuelle pour inspection
export const getLogConfig = () => ({...currentConfig, isDevelopment});

/**
 * Fonction globale pour activer temporairement tous les logs en développement
 * Utilisation en console: window.enableWidgetDebug()
 */
if (isDevelopment && typeof window !== 'undefined') {
  window.enableWidgetDebug = () => {
    configureLogging({
      api: true,
      error: true,
      warn: true,
      info: true,
      debug: true,
    });
  };

  window.disableWidgetDebug = () => {
    configureLogging({
      api: true,
      error: true,
      warn: true,
      info: false,
      debug: false,
    });
  };

  window.getWidgetLogStats = () => {};
}
